import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface Etiqueta {
  id: number;
  nome: string;
  cor: string;
  apelido: string | null;
  /** Quantos contatos usam (so o `GET /api/etiquetas` traz). Governa a trava de exclusao. */
  contatos?: number;
  /** Fora das telas de operacao (migration 0278). Ver `visiveis` abaixo. */
  ocultar?: boolean;
  /**
   * Etiqueta criada pelo auto-label do agente ([DA-018]) — carimba QUEM ATENDEU. So' o
   * `GET /api/etiquetas` traz. Governa a trava do "Nao cliente" no Gerenciar: marcar uma
   * dessas esconderia da Gestao de Vendas os clientes que aquela pessoa atendeu.
   */
  de_agente?: boolean;
  /**
   * Quem carrega esta etiqueta NAO E CLIENTE (migration 0280): some da Gestao de Vendas
   * inteira — abas, contadores, agenda e estatisticas — e continua normal no chat.
   * ⚠️ Diferente do `ocultar`, que e' so' exibicao: este MUDA o que a Gestao conta.
   */
  nao_cliente?: boolean;
  /**
   * "Na Gestao" (migration 0319, [DA-814] p.7): etiqueta `nao_cliente` que APARECE no seletor das
   * abas Contatos e Invalidos. Quem decide e' ele, no Gerenciar. So' o `GET /api/etiquetas` traz.
   */
  exibir_gestao?: boolean;
}

interface EtiquetasState {
  porNome: Map<string, Etiqueta>;
  /** TODAS, ocultas inclusive. So o Gerenciar usa — e' onde a caixa Ocultar vive. */
  todas: Etiqueta[];
  /**
   * As que aparecem pra quem OPERA: painel do contato, card da lista, filtros.
   * A regra mora aqui de proposito — quatro telas liam `todas` e cada uma filtrando
   * por conta propria e' como o nome do agente acabou em quatro lugares diferentes.
   */
  visiveis: Etiqueta[];
  /**
   * As que podem ser ALVO DE CAMPANHA — pedido dele em 11/09/2026, com `CANDITATOS` na mao.
   *
   * ⛔ **Nao e' a mesma lista que `visiveis`, e nao da' pra reusar uma pela outra.** A etiqueta
   * `nao_cliente` PRECISA aparecer no seletor do contato (e' por la' que ele marca alguem como
   * nao-cliente); o que ela nao pode e' ser oferecida como alvo de uma oferta promocional.
   *
   * ⚠️ E o motivo nao e' gosto, e' MEDICAO: quem carrega etiqueta `nao_cliente` entra na view
   * `chaves_nao_venda`, e a avaliacao de candidatos ja o exclui com o motivo `nao_e_cliente`.
   * Medido em 11/09/2026 — CANDITATOS, FORNECEDOR e NAO CLIENTES: **zero** contatos elegiveis
   * nas tres. Escolhe-las devolvia a lista vazia, que e' a tela oferecendo o que o servidor ja
   * ia negar (principio 5 do `sistema/CLAUDE.md`).
   */
  paraCampanha: Etiqueta[];
  atualizar: (etiquetas: Etiqueta[]) => void;
}

const Ctx = createContext<EtiquetasState>({
  porNome: new Map(),
  todas: [],
  visiveis: [],
  paraCampanha: [],
  atualizar: () => {},
});

export function EtiquetasProvider({ children }: { children: ReactNode }) {
  const [todas, setTodas] = useState<Etiqueta[]>([]);
  const [porNome, setPorNome] = useState<Map<string, Etiqueta>>(new Map());

  const atualizar = useCallback((etiquetas: Etiqueta[]) => {
    setTodas(etiquetas);
    setPorNome(new Map(etiquetas.map((e) => [e.nome, e])));
  }, []);

  const visiveis = useMemo(() => todas.filter((e) => !e.ocultar), [todas]);
  const paraCampanha = useMemo(() => visiveis.filter((e) => !e.nao_cliente), [visiveis]);

  return (
    <Ctx.Provider value={{ todas, visiveis, paraCampanha, porNome, atualizar }}>{children}</Ctx.Provider>
  );
}

export function useEtiquetas(): EtiquetasState {
  return useContext(Ctx);
}
